<!-- Super Admin (JWT type 1): viewing portal as another organization -->
<div
  *ngIf="showProxyBanner"
  class="bg-amber-100 border-b border-amber-300 text-amber-950 px-4 py-2 flex flex-wrap items-center justify-between gap-2"
>
  <span class="text-sm font-medium">
    Super Admin · Viewing as: <strong>{{ proxyOrgName || '—' }}</strong>
  </span>
  <button
    type="button"
    class="text-sm font-semibold px-3 py-1 rounded-md bg-amber-200 hover:bg-amber-300 border border-amber-400"
    (click)="exitOrganizationProxy()"
  >
    Exit proxy
  </button>
</div>

<!-- JWT Super Admin only: entry to pick an org to proxy -->
<div
  *ngIf="canUseOrgProxy && !isOrgProxyActive"
  class="bg-slate-50 border-b border-slate-200 text-slate-800 px-4 py-2 flex flex-wrap items-center justify-between gap-2"
>
  <span class="text-sm font-medium">Super Admin</span>
  <a
    routerLink="/home/company"
    class="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
  >
    Open as organization
    <span aria-hidden="true">→</span>
  </a>
</div>

<!-- Modern Header Component -->
<header class="bg-white shadow-lg border-b border-gray-200">
  <div class="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-between items-center h-20">
      
      <!-- Left Section - Logo and Welcome -->
      <div class="flex items-center space-x-8">
        <!-- Logo -->
        <div class="flex-shrink-0" [routerLink]="['/home/dashboard']">
          <img 
            *ngIf="headerLogo" 
            [src]="headerLogo" 
            alt="SigmaT" 
            class="h-14 w-auto object-contain"
            
          />
        </div>
        
        <!-- Welcome Message -->
        <div class="hidden md:block">
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl px-6 py-3 border border-blue-100">
            <div class="flex items-center space-x-3">
              <div class="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span class="text-base font-medium text-gray-700">
                {{ "WELCOME" | translate }}
                <span class="text-blue-600 font-semibold">
                  {{ userRole === "partner" ? orgName : currentUser }}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Center Section - Organization Name -->
      <div class="hidden lg:block flex-1 max-w-md mx-8">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl px-6 py-3 border border-gray-200">
          <div class="text-center">
            <span class="text-base font-semibold text-gray-800 truncate block">
              {{ orgName }}
            </span>
          </div>
        </div>
      </div>

      <!-- Right Section - Actions -->
      <div class="flex items-center space-x-6">
        
        <!-- Language Selector -->
        <div class="relative">
          <div class="flex items-center bg-white border border-gray-300 rounded-xl shadow-sm hover:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-400 transition-all duration-200">
            <div class="flex items-center px-4 py-3">
              <!-- <svg class="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path>
              </svg> -->
              <select
                [value]="currentLang"
                (change)="switchLanguage($event)"
                class="bg-transparent focus:outline-none text-base text-gray-700 font-medium cursor-pointer appearance-none pr-10"
              >
                <option value="en" class="py-2">🇺🇸 English</option>
                <option value="hi" class="py-2">🇮🇳 हिंदी (Hindi)</option>
                <option value="mr" class="py-2">🇮🇳 मराठी (Marathi)</option>
                <option value="te" class="py-2">🇮🇳 తెలుగు (Telugu)</option>
                <option value="ml" class="py-2">🇮🇳 മലയാളം (Malayalam)</option>
                <option value="tm" class="py-2">🇮🇳 தமிழ் (Tamil)</option>

              </select>
              <!-- <svg class="w-5 h-5 text-gray-400 ml-3 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg> -->
            </div>
          </div>
        </div>

        <!-- Profile Dropdown Button -->
        <div class="flex-shrink-0 relative profile-dropdown-container">
          <button
            (click)="toggleProfileDropdown()"
            class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105 active:scale-95"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
            </svg>
            {{ 'PROFILE' | translate }}
            <svg class="w-4 h-4 ml-2 transition-transform duration-200" [class.rotate-180]="showProfileDropdown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          
          <!-- Profile Dropdown Menu -->
          <div 
            *ngIf="showProfileDropdown"
            class="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-[9999]"
          >
            <button
              [routerLink]="'profile/personal-details'"
              (click)="showProfileDropdown = false"
              class="w-full px-4 py-3 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 flex items-center"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              {{ 'PROFILE' | translate }}
            </button>
            <button
              (click)="logout(); showProfileDropdown = false"
              class="w-full px-4 py-3 text-left text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 flex items-center"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
              </svg>
              {{ 'LOGOUT' | translate }}
            </button>
          </div>
        </div>
    
                 <!-- Cart Icon -->
         <div class="flex-shrink-0">
           <button
             (click)="navigateToPurchasePlan()"
             class="inline-flex items-center px-4 py-3 border border-transparent text-base font-medium rounded-xl text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105 active:scale-95 relative"
             title="Purchase Plan"
           >
          
<svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M20.0164 16.2572C19.5294 18.5297 19.2859 19.666 18.4608 20.333C17.6357 21 16.4737 21 14.1495 21H9.85053C7.52639 21 6.36432 21 5.53925 20.333C4.71418 19.666 4.47069 18.5297 3.98372 16.2572L3.55514 14.2572C2.83668 10.9043 2.47745 9.22793 3.378 8.11397C4.27855 7 5.99302 7 9.42196 7H14.5781C18.0071 7 19.7215 7 20.6221 8.11397C21.2929 8.94376 21.2647 10.0856 20.9097 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
<path d="M16 12H12M9 12H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M10 15H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M18 9L15 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M6 9L9 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg> 
          <span class="hidden sm:inline"></span>
            <!-- Cart badge (optional - can show number of items) -->
            <!-- <span class="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
              1
            </span> -->
          </button>
        </div>
        <!-- Organization Logo/Avatar: show uploaded logo when available,
             fall back to a colored tile with the org's initials when the
             logo isn't set or fails to load. -->
        <div class="flex-shrink-0">
          <div class="relative" [title]="orgName">
            <img
              *ngIf="imageUrl; else initialsAvatar"
              [src]="imageUrl"
              [alt]="orgName"
              (error)="onLogoError()"
              class="h-14 w-14 rounded-xl object-cover border-2 border-gray-200 shadow-sm hover:border-blue-400 transition-colors duration-200"
            />
            <ng-template #initialsAvatar>
              <div
                class="h-14 w-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-gray-200 shadow-sm select-none"
                [attr.aria-label]="orgName"
              >
                <span class="text-white text-lg font-semibold tracking-wide">
                  {{ orgInitials }}
                </span>
              </div>
            </ng-template>
          </div>
        </div>


        <!-- Mobile Menu Button (for responsive) -->
        <div class="md:hidden">
          <button
            type="button"
            (click)="toggleMobileSidebar()"
            class="inline-flex items-center justify-center p-3 rounded-xl text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors duration-200"
          >
            <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Welcome Message (hidden on desktop) -->
    <div class="md:hidden py-4 border-t border-gray-200">
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl px-6 py-3 border border-blue-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span class="text-base font-medium text-gray-700">
              {{ "WELCOME" | translate }}
              <span class="text-blue-600 font-semibold">
                {{ userRole === "partner" ? orgName : currentUser }}
              </span>
            </span>
          </div>
          <span class="text-sm text-gray-500 font-medium">
            {{ orgName }}
          </span>
        </div>
      </div>
    </div>
  </div>
</header>
